import { Loading } from 'quasar'
import { isObjectLike, isFunction, isArray } from 'lodash-es'
import { AppImage } from 'src/classes/appImage.js'
import { AppFile } from 'src/classes/appFile.js'

// setTimeout that returns a promise containing the result of the provided function
export function timeout(fn, delay) {
    return new Promise(resolve => setTimeout(() => resolve(fn()), delay))
}

// Show a fullscreen loading indicator for the duration of the given fn
export async function showLoading(fn, opts = {}) {
    Loading.show(opts)

    try {
        return await fn()
    } finally {
        Loading.hide()
    }
}

/**
 * Helper for enumerating an object as key/value pairs and attaching them
 * to the given formData object.
 */
export function attachToFormData(valueToAttach, formDataKey = null, formData = null) {
    if (!formData) formData = new FormData()
    const isMediaObject = isObjectLike(valueToAttach) && valueToAttach.id && valueToAttach.disk === 'media'

    if (valueToAttach === undefined) {
        // dont attach undefined values
        return
    } else if (!isObjectLike(valueToAttach)) {
        // scalar value
        formData.append(formDataKey, valueToAttach)
    } else if (valueToAttach instanceof dayjs) {
        // dayjs object
        formData.append(formDataKey, valueToAttach.toISOString())
    } else if (valueToAttach instanceof AppFile || valueToAttach instanceof AppImage || isMediaObject) {
        // custom app file object (only need to upload if a new file has been selected)
        if (valueToAttach.file) {
            formData.append(formDataKey, valueToAttach.file)
        }
    } else if (valueToAttach instanceof File || valueToAttach instanceof Blob) {
        // js file object
        formData.append(formDataKey, valueToAttach)
    } else if (isArray(valueToAttach) && valueToAttach.length === 0) {
        formData.append(formDataKey, [])
    } else {
        // A custom object with a getter function?
        if (isFunction(valueToAttach.get)) {
            valueToAttach = valueToAttach.get()
        }
        // either array or object of key/values we want to recursively attach
        for (const key in valueToAttach) {
            let subKey = formDataKey ? `${formDataKey}[${key}]` : key
            attachToFormData(valueToAttach[key], subKey, formData)
        }
    }

    return formData
}

/**
 * Filter an array of objects from the given fields based on the given search term
 * @param {Array} arr The source array to be filtered
 * @param {Array} fields An array of fields that can be searched on
 * @param {string} term The search term to filter
 */
export function keywordSearch(arr, fields, term) {
    let tokens = term.trim().toLowerCase().split(/\s+/)
    return arr.filter(item => fields.some(field => {
        if (!item[field]) return

        let str = String(item[field]).toLowerCase()
        return tokens.find(token => str.includes(token))
    }))
}

/**
 * Find an element in the array based on given predicate function and remove it
 * Returns null if nothing found
 */
export function findAndRemove(arr, predicate) {
    let idx = arr.findIndex(predicate)
    if (idx === -1) return
    return arr.splice(idx, 1)[0]
}

/**
 * Add/remove the given value from the given array depending on whether
 * it already exists in the array
 */
export function arrayToggle(arr, val, comparator, reInsert = false) {
    let idx = arr.findIndex(comparator || (i => i == val))
    if (idx === -1) {
        arr.push(val)
    } else if (reInsert && val) {
        arr.splice(idx, 1, val)
    } else {
        arr.splice(idx, 1)
    }
    return arr
}

/**
 * Compare 2 floats/numbers
 */
export function isSameNumber(x, y) {
    return Math.abs(x - y) < Number.EPSILON
}

/**
 * Maps a media object from the server to the correct file template object
 */
export function fileTemplateFromMedia(media) {
    if (!media) return null

    // Already mapped to appropriate template?
    if (media instanceof AppFile || media instanceof AppImage) return media

    let imageMimes = ['image/jpeg', 'image/png', 'image/gif', 'image/tiff']
    if (imageMimes.indexOf(media.mime_type) !== -1) {
        return new AppImage(media)
    }
    return new AppFile(media)
}

/**
 * Maps a native js file object one of our file template objects
 */
export function fileToTemplate(jsFile) {
    if (jsFile.type.match('image.*')) {
        let appImage = new AppImage()
        return appImage.loadFile(jsFile)
    } else {
        return Promise.resolve(new AppFile(jsFile))
    }
}

/**
 * Bulk reset a bunch of fields on a form object
 * Can optionally pass an object with some defaults to reset back to
 */
export function resetForm(form, defaults = {}) {
    for (const key in form) {
        form[key] = Object.hasOwn(defaults, key) ? defaults[key] : null
    }
}

/**
 * Pull out a specific field from an array
 */
export function pluck(rows, field = 'id') {
    return rows
        ? rows.map(m => m[field])
        : []
}

/**
 * Pull a specific field out of an array and reduce them down to a single comma delimited string
 */
export function commaSeparated(rows, field = 'name') {
    if (!rows) return ''
    return rows.map(row => trans(row[field])).join(', ')
}

export function scrollToAnchor(anchor) {
    let $el = document.getElementById(anchor)
    if ($el) {
        $el.scrollIntoView({ behavior: 'smooth' })
        $el.dispatchEvent(new CustomEvent('ripple'))
    }
}

/**
 * Execute the given function and set the given ref to true while it is busy executing
 */
export function trackBusyState(stateRef, fn) {
    stateRef.value = true

    let res = fn()
    if (!(res instanceof Promise)) throw 'trackBusyState() expected provided fn to return a promise!'
    return res.finally(() => stateRef.value = false)
}

/**
 * Helper for sorting an array of objects by one of their string fields
 * Usage: arr.sort(stringSort('name'))
 */
export function stringSort(field) {
    return (a, b) => a[field]?.localeCompare(b[field])
}

/**
 * Short-cut way of creating an populating a new JS Map() object from an array
 * @param {Array} arr Input array to create map items from
 * @param {*} keyToUse A string to use for keying the map. A field from each of the input array objects.
 * @param {*} fnValueFormatter A callback to create the value that should go into the map
 * @returns {Map} A new Map with its items populated from input arr
 */
export function createMapFrom(arr, keyToUse, fnValueFormatter = null) {
    let out = new Map()

    // Noop the formatter if it is not provided
    if (!isFunction(fnValueFormatter)) fnValueFormatter = item => item

    if (arr) {
        for (const item of arr) {
            out.set(item[keyToUse], fnValueFormatter(item, item[keyToUse]))
        }
    }

    return out
}

export function sumOf(obj, propsToSum) {
    return propsToSum.reduce((acc, prop) => acc + (obj[prop] || 0), 0)
}

